import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { captureClockPhoto } from "../lib/photoUpload";
import {
  supabase,
  clockIn as apiClockIn,
  updateTimeEntry,
  saveAllocations,
  addProduction,
  getTimeEntryEditHistory,
} from "../lib/supabase";
import { VoiceButton } from "../components/voice";
import { useOfflineQueue } from "../hooks/useOfflineQueue";
import { OfflineBanner, PendingBadge } from "../components/OfflineBanner";

/**
 * Edit indicator component - shows when a time entry has been edited
 */
function EditIndicator({ entry, editHistoryMap, onLoadHistory }) {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [tooltipPosition, setTooltipPosition] = React.useState({
    top: 0,
    left: 0,
  });
  const indicatorRef = React.useRef(null);

  if (!entry?.edited_at) return null;

  const history = editHistoryMap?.[entry.id] || [];

  const handleMouseEnter = () => {
    if (indicatorRef.current) {
      const rect = indicatorRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + 8,
        left: Math.max(10, rect.left - 100),
      });
    }
    setShowTooltip(true);
    if (!editHistoryMap?.[entry.id] && onLoadHistory) {
      onLoadHistory(entry.id);
    }
  };

  const formatFieldName = (field) => {
    if (field === "clock_in") return "Clock In";
    if (field === "clock_out") return "Clock Out";
    return field;
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return "—";
    try {
      return new Date(isoStr).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return isoStr;
    }
  };

  return (
    <span
      ref={indicatorRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowTooltip(false)}
      style={{
        marginLeft: "6px",
        padding: "2px 6px",
        fontSize: "11px",
        fontWeight: "500",
        background: "#fef3c7",
        color: "#92400e",
        borderRadius: "4px",
        cursor: "pointer",
      }}
    >
      edited
      {showTooltip && (
        <div
          style={{
            position: "fixed",
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 10000,
            minWidth: "220px",
            maxWidth: "300px",
          }}
        >
          <div
            style={{ fontWeight: "600", marginBottom: "8px", color: "#334155" }}
          >
            Edit History
          </div>
          {history.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: "12px" }}>Loading...</div>
          ) : (
            history.map((h, i) => (
              <div
                key={i}
                style={{
                  fontSize: "12px",
                  padding: "6px 0",
                  borderBottom:
                    i < history.length - 1 ? "1px solid #f1f5f9" : "none",
                }}
              >
                <div style={{ color: "#64748b" }}>
                  {formatFieldName(h.field_name)}: {formatTime(h.old_value)} →{" "}
                  {formatTime(h.new_value)}
                </div>
                <div
                  style={{
                    color: "#94a3b8",
                    fontSize: "11px",
                    marginTop: "2px",
                  }}
                >
                  {h.editor?.display_name || h.editor?.first_name || "Unknown"}{" "}
                  • {new Date(h.edited_at).toLocaleString()}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </span>
  );
}

/**
 * Fuzzy match helper - checks if two names might be the same person
 */
function fuzzyMatchName(name1, name2) {
  if (!name1 || !name2) return false;
  const n1 = name1.toLowerCase().trim();
  const n2 = name2.toLowerCase().trim();

  // Direct match
  if (n1 === n2) return true;

  // One contains the other
  if (n1.includes(n2) || n2.includes(n1)) return true;

  // First name match (handle "Last, First" and "First Last" formats)
  const parts1 = n1.includes(",")
    ? n1
        .split(",")
        .map((s) => s.trim())
        .reverse()
    : n1.split(" ");
  const parts2 = n2.includes(",")
    ? n2
        .split(",")
        .map((s) => s.trim())
        .reverse()
    : n2.split(" ");

  // Check if first names match
  if (
    parts1[0] &&
    parts2[0] &&
    (parts1[0].includes(parts2[0]) || parts2[0].includes(parts1[0]))
  ) {
    return true;
  }

  return false;
}

/**
 * TimeEditPreview - Shows extracted time edits for confirmation before saving
 * Now handles: employees not clocked in, employees not on roster
 */
function TimeEditPreview({
  result,
  crew,
  allEmployees,
  onConfirm,
  onCancel,
  onAddToRoster,
}) {
  const [editedTimeChanges, setEditedTimeChanges] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [employeePickerIndex, setEmployeePickerIndex] = useState(null); // index of edit needing picker

  const { transcript, extracted, confidence, needs_review } = result || {};
  const issues = extracted?.issues || [];
  const confidencePercent = Math.round((confidence || 0) * 100);

  // Initialize edited time changes from extracted data
  useEffect(() => {
    // Handle default times that apply to everyone
    const defaultClockIn = extracted?.default_clock_in;
    const defaultClockOut = extracted?.default_clock_out;
    const defaultHours = extracted?.default_hours;

    // Early return only if there's nothing to process
    const hasEdits = extracted?.edits && extracted.edits.length > 0;
    const hasDefaults = defaultClockIn || defaultClockOut || defaultHours;
    if (!hasEdits && !hasDefaults) return;

    // If there are default times but no individual edits, apply to all crew
    let editsToProcess = extracted?.edits || [];

    // Check if any edit is for "everyone" / "all" / "whole crew" / "the crew" / "all crew"
    const everyoneEdit = editsToProcess.find((e) =>
      /^(everyone|everybody|all|whole crew|the crew|crew|all crew|the whole crew)$/i.test(
        (e.employee_name || "").trim(),
      ),
    );

    if (everyoneEdit) {
      // Expand "everyone" to all crew members
      editsToProcess = crew.map((member) => ({
        employee_name: member.name,
        clock_in: everyoneEdit.clock_in || defaultClockIn,
        clock_out: everyoneEdit.clock_out || defaultClockOut,
        total_hours: everyoneEdit.total_hours || defaultHours,
        notes: everyoneEdit.notes,
      }));
    } else if (
      editsToProcess.length === 0 &&
      (defaultClockIn || defaultClockOut || defaultHours)
    ) {
      // Create edits for all crew members
      editsToProcess = crew.map((member) => ({
        employee_name: member.name,
        clock_in: defaultClockIn,
        clock_out: defaultClockOut,
        total_hours: defaultHours,
      }));
    }

    const matched = editsToProcess.map((edit) => {
      // Find matching employee in today's crew
      const employee = crew.find((e) =>
        fuzzyMatchName(e.name, edit.employee_name),
      );

      // If not found in crew, search all org employees for potential matches
      let potentialMatches = [];
      let needsRosterAdd = false;

      if (!employee && allEmployees && allEmployees.length > 0) {
        potentialMatches = allEmployees.filter((e) => {
          const fullName = e.display_name || `${e.first_name} ${e.last_name}`;
          return fuzzyMatchName(fullName, edit.employee_name);
        });
        needsRosterAdd = potentialMatches.length > 0;
      }

      // Calculate clock out from hours if provided but no explicit clock_out
      let calculatedClockOut = edit.clock_out;
      if (
        !calculatedClockOut &&
        edit.total_hours &&
        (edit.clock_in || employee?.clockIn)
      ) {
        const startTime = edit.clock_in || employee?.clockIn;
        if (startTime) {
          // Parse start time and add hours
          const [hours, mins] = startTime.split(":").map(Number);
          const startMins = hours * 60 + mins;
          const endMins = startMins + edit.total_hours * 60;
          const endHours = Math.floor(endMins / 60);
          const endMinutes = endMins % 60;
          calculatedClockOut = `${endHours.toString().padStart(2, "0")}:${endMinutes.toString().padStart(2, "0")}`;
        }
      }

      // Check if employee is on roster but not clocked in
      const needsClockIn = employee && !employee.timeEntry;

      return {
        ...edit,
        matchedEmployee: employee,
        potentialMatches,
        needsRosterAdd,
        needsClockIn,
        clock_in:
          edit.clock_in ||
          (defaultClockIn && !edit.clock_out ? defaultClockIn : null),
        clock_out:
          calculatedClockOut || (defaultClockOut ? defaultClockOut : null),
        hasWarning: !employee || edit.notes || needsClockIn,
      };
    });

    setEditedTimeChanges(matched);
  }, [extracted, crew, allEmployees]);

  if (!result) return null;

  const updateTimeChange = (index, field, value) => {
    setEditedTimeChanges((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeTimeChange = (index) => {
    setEditedTimeChanges((prev) => prev.filter((_, i) => i !== index));
  };

  const handleConfirm = () => {
    // Pass both time edits and allocations (if present in extracted data)
    onConfirm(editedTimeChanges, extracted?.allocations || []);
  };

  return (
    <div
      style={{
        background: "white",
        borderRadius: "16px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px",
          borderBottom: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "16px",
            fontWeight: "600",
            color: "#1e293b",
          }}
        >
          Time Edits
        </h3>
        <span
          style={{
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: "600",
            background: needs_review ? "#fef3c7" : "#dcfce7",
            color: needs_review ? "#92400e" : "#166534",
          }}
        >
          {needs_review ? "Review Changes" : "Auto-verified"}
        </span>
      </div>

      <div style={{ padding: "16px", maxHeight: "60vh", overflowY: "auto" }}>
        {/* Transcript */}
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontSize: "11px",
              fontWeight: "600",
              color: "#64748b",
              textTransform: "uppercase",
              marginBottom: "6px",
            }}
          >
            What you said
          </label>
          <p
            style={{
              margin: 0,
              padding: "12px",
              background: "#f8fafc",
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              fontSize: "14px",
              color: "#475569",
              fontStyle: "italic",
            }}
          >
            "{transcript || "No transcript available"}"
          </p>
        </div>

        {/* Time Changes - Editable */}
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontSize: "11px",
              fontWeight: "600",
              color: "#64748b",
              textTransform: "uppercase",
              marginBottom: "6px",
            }}
          >
            Time Changes ({editedTimeChanges.length} people) - Tap to edit
          </label>
          <div
            style={{
              background: "#f0f9ff",
              borderRadius: "10px",
              border: "1px solid #bae6fd",
              overflow: "hidden",
            }}
          >
            {editedTimeChanges.length === 0 ? (
              <div
                style={{
                  padding: "16px",
                  textAlign: "center",
                  color: "#94a3b8",
                  fontSize: "14px",
                }}
              >
                No time changes extracted
              </div>
            ) : (
              editedTimeChanges.map((edit, i) => (
                <div
                  key={i}
                  style={{
                    borderBottom:
                      i < editedTimeChanges.length - 1
                        ? "1px solid #bae6fd"
                        : "none",
                  }}
                >
                  {editingIndex === i ? (
                    // Edit mode
                    <div
                      style={{
                        padding: "12px",
                        background: "#e0f2fe",
                      }}
                    >
                      <div style={{ marginBottom: "10px" }}>
                        <label
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                            display: "block",
                            marginBottom: "4px",
                          }}
                        >
                          Employee
                        </label>
                        <div
                          style={{
                            fontSize: "15px",
                            fontWeight: "600",
                            color: "#1e293b",
                          }}
                        >
                          {edit.matchedEmployee?.name || edit.employee_name}
                          {edit.matchedEmployee && (
                            <span
                              style={{
                                fontWeight: "400",
                                color: "#64748b",
                                marginLeft: "8px",
                              }}
                            >
                              (current:{" "}
                              {edit.matchedEmployee.clockIn || "not in"} -{" "}
                              {edit.matchedEmployee.clockOut || "still in"})
                            </span>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          marginBottom: "10px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              fontSize: "12px",
                              color: "#64748b",
                              display: "block",
                              marginBottom: "4px",
                            }}
                          >
                            Clock In
                          </label>
                          <input
                            type="time"
                            value={edit.clock_in || ""}
                            onChange={(e) =>
                              updateTimeChange(
                                i,
                                "clock_in",
                                e.target.value || null,
                              )
                            }
                            style={{
                              width: "100%",
                              padding: "10px",
                              borderRadius: "8px",
                              border: "1px solid #cbd5e1",
                              fontSize: "16px",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label
                            style={{
                              fontSize: "12px",
                              color: "#64748b",
                              display: "block",
                              marginBottom: "4px",
                            }}
                          >
                            Clock Out
                          </label>
                          <input
                            type="time"
                            value={edit.clock_out || ""}
                            onChange={(e) =>
                              updateTimeChange(
                                i,
                                "clock_out",
                                e.target.value || null,
                              )
                            }
                            style={{
                              width: "100%",
                              padding: "10px",
                              borderRadius: "8px",
                              border: "1px solid #cbd5e1",
                              fontSize: "16px",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          onClick={() => removeTimeChange(i)}
                          style={{
                            padding: "8px 12px",
                            borderRadius: "8px",
                            border: "1px solid #fca5a5",
                            background: "#fee2e2",
                            color: "#dc2626",
                            fontSize: "13px",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          Remove
                        </button>
                        <button
                          onClick={() => setEditingIndex(null)}
                          style={{
                            flex: 1,
                            padding: "8px 12px",
                            borderRadius: "8px",
                            border: "none",
                            background: "#1a5a8e",
                            color: "white",
                            fontSize: "13px",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Display mode - tap to edit
                    <div
                      onClick={() => setEditingIndex(i)}
                      style={{
                        padding: "12px",
                        background: edit.hasWarning ? "#fffbeb" : "transparent",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "15px",
                              fontWeight: "600",
                              color: edit.matchedEmployee
                                ? "#1e293b"
                                : edit.potentialMatches?.length > 0
                                  ? "#ea580c"
                                  : "#dc2626",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              flexWrap: "wrap",
                            }}
                          >
                            {edit.matchedEmployee
                              ? edit.matchedEmployee.name
                              : edit.employee_name}
                            {!edit.matchedEmployee &&
                              edit.potentialMatches?.length > 0 && (
                                <span
                                  style={{
                                    fontSize: "11px",
                                    background: "#fef3c7",
                                    color: "#92400e",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                  }}
                                >
                                  {edit.potentialMatches.length} match
                                  {edit.potentialMatches.length > 1 ? "es" : ""}
                                </span>
                              )}
                            {!edit.matchedEmployee &&
                              !edit.potentialMatches?.length && (
                                <span
                                  style={{
                                    fontSize: "11px",
                                    background: "#fee2e2",
                                    color: "#dc2626",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                  }}
                                >
                                  Not found
                                </span>
                              )}
                            {edit.needsClockIn && (
                              <span
                                style={{
                                  fontSize: "11px",
                                  background: "#dbeafe",
                                  color: "#1d4ed8",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                }}
                              >
                                Will clock in
                              </span>
                            )}
                            {edit.needsRosterAdd && edit.selectedEmployee && (
                              <span
                                style={{
                                  fontSize: "11px",
                                  background: "#dcfce7",
                                  color: "#166534",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                }}
                              >
                                Will add to job
                              </span>
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#64748b",
                              marginTop: "4px",
                            }}
                          >
                            {edit.clock_in ||
                              edit.matchedEmployee?.clockIn ||
                              "—"}{" "}
                            → {edit.clock_out || "—"}
                            {edit.total_hours && (
                              <span
                                style={{
                                  marginLeft: "8px",
                                  color: "#1a5a8e",
                                  fontWeight: "500",
                                }}
                              >
                                ({edit.total_hours}h)
                              </span>
                            )}
                          </div>
                        </div>
                        <span style={{ color: "#94a3b8", fontSize: "14px" }}>
                          ✏️
                        </span>
                      </div>
                      {/* Show potential matches that need selection */}
                      {!edit.matchedEmployee &&
                        edit.potentialMatches?.length > 0 &&
                        !edit.selectedEmployee && (
                          <div
                            style={{
                              marginTop: "8px",
                              padding: "8px",
                              background: "#fef3c7",
                              borderRadius: "8px",
                              border: "1px solid #fcd34d",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#92400e",
                                marginBottom: "6px",
                              }}
                            >
                              Select the correct employee:
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "6px",
                              }}
                            >
                              {edit.potentialMatches.map((emp) => (
                                <button
                                  key={emp.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Select this employee
                                    setEditedTimeChanges((prev) => {
                                      const updated = [...prev];
                                      updated[i] = {
                                        ...updated[i],
                                        selectedEmployee: emp,
                                        matchedEmployee: null, // Will be set after roster add
                                      };
                                      return updated;
                                    });
                                  }}
                                  style={{
                                    padding: "6px 10px",
                                    borderRadius: "6px",
                                    border: "1px solid #fcd34d",
                                    background: "white",
                                    fontSize: "13px",
                                    fontWeight: "500",
                                    color: "#92400e",
                                    cursor: "pointer",
                                  }}
                                >
                                  {emp.first_name} {emp.last_name}
                                </button>
                              ))}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeTimeChange(i);
                                }}
                                style={{
                                  padding: "6px 10px",
                                  borderRadius: "6px",
                                  border: "1px solid #fca5a5",
                                  background: "#fee2e2",
                                  fontSize: "13px",
                                  fontWeight: "500",
                                  color: "#dc2626",
                                  cursor: "pointer",
                                }}
                              >
                                Skip
                              </button>
                            </div>
                          </div>
                        )}
                      {/* Show selected employee (pending roster add) */}
                      {edit.selectedEmployee && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#166534",
                            background: "#dcfce7",
                            padding: "6px 8px",
                            borderRadius: "6px",
                            marginTop: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <span>
                            ✓ {edit.selectedEmployee.first_name}{" "}
                            {edit.selectedEmployee.last_name} will be added to
                            job
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditedTimeChanges((prev) => {
                                const updated = [...prev];
                                updated[i] = {
                                  ...updated[i],
                                  selectedEmployee: null,
                                };
                                return updated;
                              });
                            }}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#166534",
                              cursor: "pointer",
                              fontSize: "14px",
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      )}
                      {edit.notes && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#ea580c",
                            background: "#fff7ed",
                            padding: "6px 8px",
                            borderRadius: "6px",
                            marginTop: "6px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>📝</span> {edit.notes}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Issues */}
        {issues.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "11px",
                fontWeight: "600",
                color: "#64748b",
                textTransform: "uppercase",
                marginBottom: "6px",
              }}
            >
              Issues Noted
            </label>
            <div
              style={{
                background: "#fef3c7",
                borderRadius: "10px",
                padding: "12px",
                border: "1px solid #fcd34d",
              }}
            >
              {issues.map((issue, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: "14px",
                    color: "#92400e",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginBottom: i < issues.length - 1 ? "6px" : 0,
                  }}
                >
                  <span>⚠️</span> {issue}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Confidence indicator */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "600",
              color: "#64748b",
              textTransform: "uppercase",
            }}
          >
            Confidence
          </span>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                flex: 1,
                background: "#e2e8f0",
                borderRadius: "4px",
                height: "8px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  borderRadius: "4px",
                  background:
                    confidence > 0.8
                      ? "#22c55e"
                      : confidence > 0.5
                        ? "#eab308"
                        : "#ef4444",
                  width: `${confidencePercent}%`,
                  transition: "width 0.3s",
                }}
              />
            </div>
            <span
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#475569",
                minWidth: "40px",
              }}
            >
              {confidencePercent}%
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          padding: "16px",
          borderTop: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <button
          onClick={onCancel}
          style={{
            flex: 1,
            padding: "12px 16px",
            borderRadius: "10px",
            border: "1px solid #e2e8f0",
            background: "white",
            fontSize: "14px",
            fontWeight: "600",
            color: "#64748b",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>

        <button
          onClick={handleConfirm}
          disabled={
            editedTimeChanges.filter(
              (e) => e.matchedEmployee || e.selectedEmployee,
            ).length === 0
          }
          style={{
            flex: 2,
            padding: "12px 16px",
            borderRadius: "10px",
            border: "none",
            background:
              editedTimeChanges.filter(
                (e) => e.matchedEmployee || e.selectedEmployee,
              ).length === 0
                ? "#cbd5e1"
                : "#1a5a8e",
            fontSize: "14px",
            fontWeight: "600",
            color: "white",
            cursor:
              editedTimeChanges.filter(
                (e) => e.matchedEmployee || e.selectedEmployee,
              ).length === 0
                ? "not-allowed"
                : "pointer",
          }}
        >
          Update Times (
          {
            editedTimeChanges.filter(
              (e) => e.matchedEmployee || e.selectedEmployee,
            ).length
          }{" "}
          people)
        </button>
      </div>
    </div>
  );
}

export default function Dashboard({
  job,
  currentUser,
  crew,
  orgId,
  vocabulary = [],
  onClockIn,
  onClockOut,
  onBulkClockIn,
  onBulkClockOut,
  onUpdateTime,
  onNavigate,
  onChangeJob,
  onMenuOpen,
  onCrewAdded,
}) {
  const [isClocking, setIsClocking] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editClockIn, setEditClockIn] = useState("");
  const [editClockOut, setEditClockOut] = useState("");

  // Voice time edit state
  const [voiceResult, setVoiceResult] = useState(null);
  const [voiceProcessing, setVoiceProcessing] = useState(false);

  // Universal voice state
  const [universalVoiceResult, setUniversalVoiceResult] = useState(null);

  // Offline queue state
  const { isOnline, isSyncing, pendingCount, pendingByType } =
    useOfflineQueue();

  // Add crew modal state
  const [showAddCrew, setShowAddCrew] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [addingEmployee, setAddingEmployee] = useState(null);

  // Cost codes modal state
  const [showCostCodes, setShowCostCodes] = useState(false);
  const [allCostCodes, setAllCostCodes] = useState([]);
  const [jobCostCodes, setJobCostCodes] = useState([]);
  const [loadingCostCodes, setLoadingCostCodes] = useState(false);
  const [addingCostCode, setAddingCostCode] = useState(null);

  // Edit history state for tracking time entry edits
  const [editHistoryMap, setEditHistoryMap] = useState({});

  const loadEditHistory = async (timeEntryId) => {
    if (editHistoryMap[timeEntryId]) return;
    try {
      const history = await getTimeEntryEditHistory(timeEntryId);
      setEditHistoryMap((prev) => ({ ...prev, [timeEntryId]: history }));
    } catch (error) {
      console.error("Error loading edit history:", error);
    }
  };

  // Load all employees when modal opens
  useEffect(() => {
    if (showAddCrew && orgId) {
      loadAllEmployees();
    }
  }, [showAddCrew, orgId]); // loadAllEmployees is stable - defined in component scope

  const loadAllEmployees = async () => {
    setLoadingEmployees(true);
    try {
      // Query 1: Get employees directly in this org
      const { data: directEmployees, error: error1 } = await supabase
        .from("t4s_employees")
        .select("*")
        .eq("org_id", orgId)
        .eq("is_active", true)
        .order("last_name");

      // Query 2: Get employees linked via t4s_employee_orgs
      const { data: linkedEmployees, error: error2 } = await supabase
        .from("t4s_employees")
        .select("*, t4s_employee_orgs!inner(org_id)")
        .eq("t4s_employee_orgs.org_id", orgId)
        .eq("is_active", true)
        .order("last_name");

      if (error1) console.error("Error loading direct employees:", error1);
      if (error2) console.error("Error loading linked employees:", error2);

      // Combine and deduplicate
      const allEmployees = [
        ...(directEmployees || []),
        ...(linkedEmployees || []),
      ];
      const seen = new Set();
      const unique = allEmployees.filter((emp) => {
        if (seen.has(emp.id)) return false;
        seen.add(emp.id);
        return true;
      });

      // Sort by last name
      unique.sort((a, b) =>
        (a.last_name || "").localeCompare(b.last_name || ""),
      );

      setAllEmployees(unique);
    } catch (err) {
      console.error("Error loading employees:", err);
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Add employee to today's crew
  const handleAddToCrew = async (employee) => {
    setAddingEmployee(employee.id);
    try {
      const today = new Date().toLocaleDateString("en-CA");

      // Create assignment for today (use rawId for actual UUID)
      const { error } = await supabase.from("t4s_job_assignments").insert({
        org_id: orgId,
        job_id: job.rawId || job.id,
        employee_id: employee.id,
        assigned_date: today,
      });

      if (error) {
        if (error.code === "23505") {
          toast.info("Already assigned to this job today");
        } else {
          throw error;
        }
      } else {
        // Refresh crew list
        onCrewAdded?.();
        setShowAddCrew(false);
      }
    } catch (err) {
      console.error("Error adding to crew:", err);
      toast.error("Error adding crew member");
    } finally {
      setAddingEmployee(null);
    }
  };

  // Filter out employees already on crew
  const crewIds = new Set(crew.map((c) => c.id));
  const availableEmployees = allEmployees.filter((e) => !crewIds.has(e.id));

  // Load cost codes when modal opens
  useEffect(() => {
    if (showCostCodes && orgId) {
      loadCostCodes();
    }
  }, [showCostCodes, orgId]); // loadCostCodes is stable - defined in component scope

  const loadCostCodes = async () => {
    setLoadingCostCodes(true);
    try {
      // Load all org cost codes
      const { data: allCodes, error: allError } = await supabase
        .from("t4s_cost_codes")
        .select("*")
        .eq("org_id", orgId)
        .eq("is_active", true)
        .order("code");

      if (allError) throw allError;
      setAllCostCodes(allCodes || []);

      // Load job's current cost codes
      const { data: jobCodes, error: jobError } = await supabase
        .from("t4s_job_cost_codes")
        .select("*, cost_code:t4s_cost_codes(*)")
        .eq("job_id", job.rawId || job.id)
        .eq("is_active", true)
        .order("sort_order");

      if (jobError) throw jobError;
      setJobCostCodes(jobCodes || []);
    } catch (err) {
      console.error("Error loading cost codes:", err);
    } finally {
      setLoadingCostCodes(false);
    }
  };

  const handleAddCostCode = async (costCode) => {
    setAddingCostCode(costCode.id);
    try {
      const maxSort = jobCostCodes.reduce(
        (max, jcc) => Math.max(max, jcc.sort_order || 0),
        0,
      );

      const { error } = await supabase.from("t4s_job_cost_codes").insert({
        job_id: job.rawId || job.id,
        cost_code_id: costCode.id,
        source: "custom",
        sort_order: maxSort + 1,
      });

      if (error) {
        if (error.code === "23505") {
          toast.info("This cost code is already on the job");
        } else {
          throw error;
        }
      } else {
        await loadCostCodes();
      }
    } catch (err) {
      console.error("Error adding cost code:", err);
      toast.error("Error adding cost code");
    } finally {
      setAddingCostCode(null);
    }
  };

  const handleRemoveCostCode = async (jobCostCodeId) => {
    try {
      const { error } = await supabase
        .from("t4s_job_cost_codes")
        .delete()
        .eq("id", jobCostCodeId);

      if (error) throw error;
      await loadCostCodes();
    } catch (err) {
      console.error("Error removing cost code:", err);
      toast.error("Error removing cost code");
    }
  };

  // Filter out cost codes already on job
  const jobCostCodeIds = new Set(jobCostCodes.map((jcc) => jcc.cost_code_id));
  const availableCostCodes = allCostCodes.filter(
    (cc) => !jobCostCodeIds.has(cc.id),
  );

  const clockedInCount = crew.filter((m) => m.clockIn).length;
  const clockedOutCount = crew.filter((m) => !m.clockIn).length;

  // Handle tapping a crew member (individual clock in/out)
  const handleCrewTap = async (member) => {
    if (isClocking) return;

    const action = member.clockIn ? "out" : "in";
    setIsClocking(true);

    try {
      // Capture and upload photo (required for clock in/out)
      const { url: photoUrl } = await captureClockPhoto(
        job?.id,
        member.id,
        action,
        orgId,
      );

      // Clock in/out with the photo URL
      if (action === "in") {
        await onClockIn?.(member.id, photoUrl);
      } else {
        await onClockOut?.(member.id, photoUrl);
      }

      // Photo was captured but the upload failed (captureClockPhoto
      // swallows upload errors so the punch itself isn't blocked) - warn
      // the user so a missing photo doesn't look like a normal clock event.
      if (!photoUrl) {
        toast.warning(
          `Clock-${action} saved, photo upload failed`,
        );
      }
    } catch (e) {
      if (e.message !== "Cancelled" && e.message !== "No photo captured") {
        console.error("Clock error:", e.message);
      }
    } finally {
      setIsClocking(false);
    }
  };

  // Bulk clock all in (no photos)
  const handleClockAllIn = () => {
    onBulkClockIn?.();
  };

  // Bulk clock all out (no photos)
  const handleClockAllOut = () => {
    onBulkClockOut?.();
  };

  // Open time edit modal
  const handleEditTime = (member) => {
    setEditingMember(member);
    // Convert ISO timestamps to HH:MM format for time input
    const formatTimeForInput = (isoString) => {
      if (!isoString) return "";
      const date = new Date(isoString);
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    };
    setEditClockIn(formatTimeForInput(member.timeEntry?.clock_in));
    setEditClockOut(formatTimeForInput(member.timeEntry?.clock_out));
  };

  // Save time edits
  const handleSaveTimeEdit = async () => {
    if (!editingMember) return;
    try {
      await onUpdateTime?.(editingMember.id, {
        clockIn: editClockIn || null,
        clockOut: editClockOut || null,
      });
      setEditingMember(null);
      setEditClockIn("");
      setEditClockOut("");
    } catch (error) {
      console.error("Failed to save time edit:", error);
      toast.error("Failed to save: " + error.message);
    }
  };

  // Cancel time edit
  const handleCancelTimeEdit = () => {
    setEditingMember(null);
    setEditClockIn("");
    setEditClockOut("");
  };

  // Voice time edit handlers
  const handleVoiceError = (error) => {
    console.error("Voice error:", error);
    toast.error("Voice recording failed. Please try again.");
  };

  const handleVoiceConfirm = async (editedTimeChanges, voiceAllocations) => {
    if (!editedTimeChanges || editedTimeChanges.length === 0) {
      setVoiceResult(null);
      return;
    }

    const today = new Date().toLocaleDateString("en-CA");

    // Helper to match cost_code_hint to actual cost code
    const matchCostCode = (hint) => {
      if (!hint) return null;
      const hintLower = hint.toLowerCase();
      // Try exact match first
      let match = jobCostCodes.find(
        (jcc) =>
          jcc.cost_code?.code?.toLowerCase() === hintLower ||
          jcc.cost_code?.name?.toLowerCase() === hintLower,
      );
      // Try partial match
      if (!match) {
        match = jobCostCodes.find(
          (jcc) =>
            jcc.cost_code?.code?.toLowerCase().includes(hintLower) ||
            jcc.cost_code?.name?.toLowerCase().includes(hintLower) ||
            hintLower.includes(jcc.cost_code?.code?.toLowerCase()) ||
            hintLower.includes(jcc.cost_code?.name?.toLowerCase()),
        );
      }
      return match?.cost_code_id || null;
    };

    for (const edit of editedTimeChanges) {
      const employee = edit.matchedEmployee || edit.selectedEmployee;

      if (!employee) {
        console.warn("Skipping - no matched or selected employee:", edit);
        continue;
      }

      const employeeName =
        employee.name || `${employee.first_name} ${employee.last_name}`;
      const employeeId = employee.id;

      try {
        let timeEntryId = null;

        // Step 1: If selectedEmployee (not on job roster), add to roster first
        if (edit.selectedEmployee) {
          const { error: assignError } = await supabase
            .from("t4s_job_assignments")
            .insert({
              org_id: orgId,
              job_id: job.rawId || job.id,
              employee_id: employeeId,
              assigned_date: today,
            });

          if (assignError && assignError.code !== "23505") {
            // 23505 = already exists
            throw new Error(`Failed to add to roster: ${assignError.message}`);
          }
        }

        // Step 2: Check if employee needs clock in (no time entry for today)
        // For selectedEmployee, they definitely need a new time entry
        // For matchedEmployee with needsClockIn, create entry too
        const needsNewEntry = edit.selectedEmployee || edit.needsClockIn;

        if (needsNewEntry) {
          // Create time entry with clock in/out times
          const timeEntry = await apiClockIn({
            orgId,
            employeeId,
            jobId: job.rawId || job.id,
            jobTimezone: job.timezone || "America/Denver",
            enteredBy: currentUser?.id || null,
          });

          timeEntryId = timeEntry.id;

          // Now update with the specified times
          if (edit.clock_in || edit.clock_out) {
            await updateTimeEntry(timeEntry.id, {
              clockIn: edit.clock_in || null,
              clockOut: edit.clock_out || null,
              jobTimezone: job.timezone || "America/Denver",
              editedBy: currentUser?.id || null,
              editSource: "voice_command",
            });
          }
        } else {
          // Step 3: Employee already has a time entry, just update times
          await onUpdateTime?.(employeeId, {
            clockIn: edit.clock_in || null,
            clockOut: edit.clock_out || null,
          });
          // Get the time entry ID from crew
          const crewMember = crew.find((m) => m.id === employeeId);
          timeEntryId = crewMember?.timeEntry?.id;
        }

        // Step 4: Save allocations if present for this employee
        if (voiceAllocations && voiceAllocations.length > 0 && timeEntryId) {
          // Check if allocation is for "everyone" / "the crew" / etc OR matches this specific employee
          const isEveryoneAllocation = (name) =>
            /^(everyone|everybody|all|whole crew|the crew|crew|all crew|the whole crew)$/i.test(
              (name || "").trim(),
            );

          const employeeAllocations = voiceAllocations.filter(
            (a) =>
              isEveryoneAllocation(a.employee_name) ||
              fuzzyMatchName(a.employee_name, employeeName),
          );

          if (employeeAllocations.length > 0) {
            const allocationData = employeeAllocations
              .map((a) => ({
                costCodeId: matchCostCode(a.cost_code_hint || a.activity),
                hours: a.hours,
              }))
              .filter((a) => a.costCodeId && a.hours > 0);

            if (allocationData.length > 0) {
              await saveAllocations(
                timeEntryId,
                allocationData,
                currentUser?.id || null,
              );
            }
          }
        }
      } catch (err) {
        console.error(`Failed to process ${employeeName}:`, err);
        toast.error(`Failed to update ${employeeName}: ${err.message}`);
      }
    }

    // Refresh crew list to show new entries
    onCrewAdded?.();
    setVoiceResult(null);
  };

  const handleVoiceCancel = () => {
    setVoiceResult(null);
  };

  // Build screen context for voice time edit
  // Group crew by role/trade for voice commands like "laborers did 4 hours cleanup"
  const crewByRole = {};
  crew.forEach((m) => {
    const role = (m.role || m.type || "crew").toLowerCase();
    if (!crewByRole[role]) crewByRole[role] = [];
    crewByRole[role].push(m.name);
  });

  const voiceScreenContext = {
    crew_roster: crew.map(
      (m) =>
        `${m.name} (${m.role || m.type || "crew"}, ${m.clockIn || "not in"} - ${m.clockOut || "still in"})`,
    ),
    crew_by_role: Object.entries(crewByRole).map(
      ([role, names]) => `${role}: ${names.join(", ")}`,
    ),
    current_times: crew.map((m) => ({
      name: m.name,
      id: m.id,
      role: m.role || m.type || "crew",
      clockIn: m.clockIn,
      clockOut: m.clockOut,
      totalHours: m.totalClocked,
    })),
    job_cost_codes: jobCostCodes.map(
      (jcc) => jcc.cost_code?.name || jcc.cost_code?.code,
    ),
    vocabulary: vocabulary,
  };

  // Universal voice handlers
  const handleUniversalVoiceResult = async (result) => {
    const detectedIntent = result?.extracted?.detected_intent;

    if (!detectedIntent) {
      console.warn("No intent detected");
      setUniversalVoiceResult(result);
      return;
    }

    // Route based on detected intent
    if (detectedIntent === "time_edit") {
      // Transform to time_edit format and use existing handler
      const timeEditResult = {
        ...result,
        extracted: {
          ...result.extracted.time_edit,
          issues: result.extracted.issues,
        },
      };
      // Load employees if needed
      if (allEmployees.length === 0 && orgId) {
        await loadAllEmployees();
      }
      setVoiceResult(timeEditResult);
    } else if (detectedIntent === "time_and_allocation") {
      // Combined time edit AND allocation - transform to include both
      const combinedResult = {
        ...result,
        extracted: {
          ...result.extracted.time_edit,
          allocations: result.extracted.time_allocation?.allocations || [],
          issues: result.extracted.issues,
        },
      };
      // Load employees if needed
      if (allEmployees.length === 0 && orgId) {
        await loadAllEmployees();
      }
      setVoiceResult(combinedResult);
    } else if (detectedIntent === "time_allocation") {
      // Navigate to time allocation page with the result
      // For now, show a preview
      setUniversalVoiceResult(result);
    } else if (detectedIntent === "production") {
      // Show production preview for confirmation
      setUniversalVoiceResult(result);
    } else if (detectedIntent === "status_update") {
      // Navigate to status page or save directly
      setUniversalVoiceResult(result);
    } else if (detectedIntent === "issue") {
      setUniversalVoiceResult(result);
    } else {
      // note or unknown
      setUniversalVoiceResult(result);
    }
  };

  const handleUniversalVoiceError = (error) => {
    console.error("Universal voice error:", error);
    toast.error("Voice recording failed. Please try again.");
  };

  const handleUniversalVoiceCancel = () => {
    setUniversalVoiceResult(null);
  };

  // Handle camera tile - navigate to camera page for job photos
  const handleCameraTap = () => {
    onNavigate?.("camera");
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      {/* Offline Status Banner */}
      <OfflineBanner
        isOnline={isOnline}
        isSyncing={isSyncing}
        pendingCount={pendingCount}
      />

      {/* Header */}
      <div
        style={{
          background: "#1a5a8e",
          color: "white",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 40,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            onClick={onChangeJob}
            style={{
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: "-8px",
              borderRadius: "8px",
              background: "transparent",
              border: "none",
              color: "white",
              cursor: "pointer",
            }}
          >
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h1 style={{ fontSize: "18px", fontWeight: "600", margin: 0 }}>
            {job.id}
          </h1>
        </div>
        <button
          onClick={onMenuOpen}
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
            border: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            fontWeight: "600",
            color: "white",
            cursor: "pointer",
          }}
        >
          {currentUser?.initials || "??"}
        </button>
      </div>

      {/* Current Job Card */}
      <div style={{ padding: "16px" }}>
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "16px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            border: "1px solid #e2e8f0",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "8px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#1a5a8e",
                }}
              >
                {job.id}
              </span>
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: "6px",
                  background: "#dcfce7",
                  color: "#166534",
                  fontSize: "11px",
                  fontWeight: "600",
                  textTransform: "uppercase",
                }}
              >
                Active
              </span>
            </div>
            <button
              onClick={onChangeJob}
              style={{
                fontSize: "14px",
                color: "#1a5a8e",
                fontWeight: "500",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              Change
            </button>
          </div>
          <div style={{ color: "#475569", marginBottom: "8px" }}>
            {job.name}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              fontSize: "13px",
              color: "#94a3b8",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  background: "#10b981",
                }}
              />
              {clockedInCount} on site
            </span>
            <span>
              Day {job.day} of {job.totalDays}
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons Grid */}
      <div
        style={{
          padding: "0 16px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12px",
        }}
      >
        {/* ALLOCATE Button */}
        <button
          onClick={() => onNavigate("time")}
          style={{
            position: "relative",
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            borderRadius: "16px",
            padding: "20px",
            color: "white",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
            boxShadow: "0 4px 12px rgba(16,185,129,0.3)",
          }}
        >
          <PendingBadge count={pendingByType.TIME_ALLOCATION_ADD || 0} />
          <div style={{ fontSize: "28px", marginBottom: "8px" }}>⏱</div>
          <div style={{ fontSize: "18px", fontWeight: "600" }}>ALLOCATE</div>
          <div style={{ fontSize: "13px", opacity: 0.8 }}>Assign hours</div>
        </button>

        {/* CAMERA Button */}
        <button
          onClick={handleCameraTap}
          style={{
            position: "relative",
            background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
            borderRadius: "16px",
            padding: "20px",
            color: "white",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
            boxShadow: "0 4px 12px rgba(59,130,246,0.3)",
          }}
        >
          <PendingBadge count={pendingByType.PHOTO_ADD || 0} />
          <div style={{ fontSize: "28px", marginBottom: "8px" }}>📷</div>
          <div style={{ fontSize: "18px", fontWeight: "600" }}>CAMERA</div>
          <div style={{ fontSize: "13px", opacity: 0.8 }}>
            Auto-tagged photos
          </div>
        </button>

        {/* STATUS Button */}
        <button
          onClick={() => onNavigate("status")}
          style={{
            position: "relative",
            background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
            borderRadius: "16px",
            padding: "20px",
            color: "white",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
            boxShadow: "0 4px 12px rgba(249,115,22,0.3)",
          }}
        >
          <PendingBadge
            count={
              (pendingByType.STATUS_ADD || 0) +
              (pendingByType.PRODUCTION_ADD || 0)
            }
          />
          <div style={{ fontSize: "28px", marginBottom: "8px" }}>📋</div>
          <div style={{ fontSize: "18px", fontWeight: "600" }}>STATUS</div>
          <div style={{ fontSize: "13px", opacity: 0.8 }}>
            Updates & production
          </div>
        </button>

        {/* T&M Button */}
        <button
          style={{
            background: "linear-gradient(135deg, #ec4899 0%, #db2777 100%)",
            borderRadius: "16px",
            padding: "20px",
            color: "white",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
            boxShadow: "0 4px 12px rgba(236,72,153,0.3)",
          }}
        >
          <div style={{ fontSize: "28px", marginBottom: "8px" }}>📝</div>
          <div style={{ fontSize: "18px", fontWeight: "600" }}>T&M</div>
          <div style={{ fontSize: "13px", opacity: 0.8 }}>Material tickets</div>
        </button>
      </div>

      {/* Universal Voice Command */}
      <div style={{ padding: "16px", paddingTop: "12px" }}>
        <div
          style={{
            position: "relative",
            background: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
            borderRadius: "16px",
            padding: "16px",
            boxShadow: "0 4px 12px rgba(139,92,246,0.3)",
          }}
        >
          <PendingBadge count={pendingByType.VOICE_ADD || 0} />
          <VoiceButton
            contextType="universal"
            jobId={job?.rawId || job?.id}
            screenContext={voiceScreenContext}
            onResult={handleUniversalVoiceResult}
            onError={handleUniversalVoiceError}
            onRecordingChange={setVoiceProcessing}
            style={{
              width: "100%",
              padding: "16px",
              borderRadius: "12px",
              fontSize: "16px",
              background: "rgba(255,255,255,0.2)",
              border: "2px solid rgba(255,255,255,0.3)",
              color: "white",
            }}
            label="Voice Command"
          />
          <div
            style={{
              fontSize: "12px",
              color: "rgba(255,255,255,0.8)",
              textAlign: "center",
              marginTop: "8px",
            }}
          >
            Times, allocations, status updates, issues - just talk
          </div>
        </div>
      </div>

      {/* Today's Crew - Clock In/Out Section */}
      <div style={{ padding: "16px", marginTop: "8px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{ fontSize: "15px", fontWeight: "600", color: "#334155" }}
            >
              Today's Crew
            </span>
            <span
              style={{
                padding: "2px 10px",
                borderRadius: "999px",
                background: "#1a5a8e",
                color: "white",
                fontSize: "12px",
                fontWeight: "600",
              }}
            >
              {crew.length}
            </span>
          </div>
          <button
            onClick={() => setShowAddCrew(true)}
            style={{
              padding: "6px 12px",
              borderRadius: "8px",
              background: "#1a5a8e",
              color: "white",
              border: "none",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <span style={{ fontSize: "16px" }}>+</span> Add Crew
          </button>
        </div>

        {/* Bulk Clock Buttons */}
        <div style={{ display: "flex", gap: "12px", marginBottom: "12px" }}>
          <button
            onClick={handleClockAllIn}
            disabled={clockedOutCount === 0}
            style={{
              flex: 1,
              padding: "12px 16px",
              borderRadius: "12px",
              background: clockedOutCount > 0 ? "#10b981" : "#e2e8f0",
              color: clockedOutCount > 0 ? "white" : "#94a3b8",
              border: "none",
              fontSize: "14px",
              fontWeight: "600",
              cursor: clockedOutCount > 0 ? "pointer" : "default",
            }}
          >
            Clock All In
          </button>
          <button
            onClick={handleClockAllOut}
            disabled={clockedInCount === 0}
            style={{
              flex: 1,
              padding: "12px 16px",
              borderRadius: "12px",
              background: clockedInCount > 0 ? "#ef4444" : "#e2e8f0",
              color: clockedInCount > 0 ? "white" : "#94a3b8",
              border: "none",
              fontSize: "14px",
              fontWeight: "600",
              cursor: clockedInCount > 0 ? "pointer" : "default",
            }}
          >
            Clock All Out
          </button>
        </div>

        {/* Crew List - Tap to clock with photo */}
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            position: "relative",
            zIndex: 1,
          }}
        >
          {crew.map((member, index) => {
            const isIn = member.clockIn && !member.clockOut;
            const isComplete = member.clockIn && member.clockOut;
            return (
              <div
                key={member.id}
                style={{
                  width: "100%",
                  padding: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "transparent",
                  border: "none",
                  borderBottom:
                    index < crew.length - 1 ? "1px solid #f1f5f9" : "none",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "14px" }}
                >
                  {/* Avatar - show photo if available */}
                  <div
                    style={{
                      width: "52px",
                      height: "52px",
                      borderRadius: "50%",
                      background: member.photoUrl
                        ? `url(${member.photoUrl}) center/cover`
                        : member.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: "600",
                      fontSize: "18px",
                      border: member.photoUrl ? "2px solid #10b981" : "none",
                    }}
                  >
                    {!member.photoUrl &&
                      (member.display_name || member.name || "")
                        .split(",")[0]
                        .slice(0, 2)
                        .toUpperCase()}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        color: "#334155",
                      }}
                    >
                      {member.display_name || member.name}
                    </div>
                    <div style={{ fontSize: "15px", color: "#94a3b8" }}>
                      {member.role || member.type}
                      {member.openShiftWarning && <p role="status" style={{ color: "#92400e", fontSize: "13px" }}>{member.openShiftWarning}</p>}
                      {member.clockIn && (
                        <span style={{ marginLeft: "8px", color: "#64748b" }}>
                          • {member.clockIn}
                          {member.clockOut ? ` - ${member.clockOut}` : ""}
                          <EditIndicator
                            entry={member.timeEntry}
                            editHistoryMap={editHistoryMap}
                            onLoadHistory={loadEditHistory}
                          />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  {/* Edit button - only show if clocked in */}
                  {member.clockIn && (
                    <button
                      type="button"
                      onClick={() => handleEditTime(member)}
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "10px",
                        background: "#f1f5f9",
                        border: "none",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <svg
                        width="22"
                        height="22"
                        fill="none"
                        stroke="#64748b"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                    </button>
                  )}
                  {isComplete ? (
                    <div
                      style={{
                        padding: "12px 18px",
                        borderRadius: "10px",
                        fontSize: "16px",
                        fontWeight: "600",
                        background: "#f1f5f9",
                        color: "#64748b",
                      }}
                    >
                      Done
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleCrewTap(member)}
                      disabled={!!member.openShiftWarning}
                      style={{
                        padding: "12px 18px",
                        borderRadius: "10px",
                        fontSize: "16px",
                        fontWeight: "600",
                        background: isIn ? "#fee2e2" : "#dcfce7",
                        color: isIn ? "#dc2626" : "#166534",
                        border: "none",
                      }}
                    >
                      {member.openShiftWarning ? "Needs review" : isIn ? "Clock Out" : "Clock In"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div
          style={{
            marginTop: "8px",
            fontSize: "12px",
            color: "#94a3b8",
            textAlign: "center",
          }}
        >
          Tap crew member to clock in/out with photo
        </div>
      </div>

      {/* Time Edit Modal */}
      {editingMember && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
            }}
            onClick={handleCancelTimeEdit}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "400px",
              background: "white",
              borderRadius: "16px",
              padding: "24px",
            }}
          >
            <div style={{ marginBottom: "24px" }}>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: "#1e293b",
                }}
              >
                Edit Time
              </div>
              <div
                style={{ fontSize: "18px", color: "#64748b", marginTop: "6px" }}
              >
                {editingMember.name}
              </div>
            </div>

            <div style={{ display: "flex", gap: "16px", marginBottom: "28px" }}>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#64748b",
                    textTransform: "uppercase",
                    marginBottom: "10px",
                  }}
                >
                  Clock In
                </label>
                <input
                  type="time"
                  value={editClockIn}
                  onChange={(e) => setEditClockIn(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #e2e8f0",
                    fontSize: "20px",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#64748b",
                    textTransform: "uppercase",
                    marginBottom: "10px",
                  }}
                >
                  Clock Out
                </label>
                <input
                  type="time"
                  value={editClockOut}
                  onChange={(e) => setEditClockOut(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #e2e8f0",
                    fontSize: "20px",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={handleCancelTimeEdit}
                style={{
                  flex: 1,
                  padding: "18px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  background: "white",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#64748b",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTimeEdit}
                style={{
                  flex: 2,
                  padding: "18px",
                  borderRadius: "12px",
                  border: "none",
                  background: "#1a5a8e",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "white",
                  cursor: "pointer",
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Crew Modal */}
      {showAddCrew && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
            }}
            onClick={() => setShowAddCrew(false)}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "430px",
              margin: "0 auto",
              background: "white",
              borderRadius: "24px 24px 0 0",
              padding: "24px",
              paddingBottom: "40px",
              maxHeight: "70vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "4px",
                background: "#cbd5e1",
                borderRadius: "2px",
                margin: "0 auto 16px",
              }}
            />

            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                }}
              >
                Add Crew Member
              </div>
              <div
                style={{ fontSize: "14px", color: "#64748b", marginTop: "4px" }}
              >
                Select employee to add to today's crew
              </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto" }}>
              {loadingEmployees ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#64748b",
                  }}
                >
                  Loading...
                </div>
              ) : availableEmployees.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#64748b",
                  }}
                >
                  No available employees
                </div>
              ) : (
                availableEmployees.map((emp) => (
                  <button
                    key={emp.id}
                    onClick={() => handleAddToCrew(emp)}
                    disabled={addingEmployee === emp.id}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      background:
                        addingEmployee === emp.id ? "#f1f5f9" : "transparent",
                      border: "none",
                      borderBottom: "1px solid #f1f5f9",
                      cursor: addingEmployee ? "wait" : "pointer",
                      textAlign: "left",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          background: emp.avatar_color || "#64748b",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontWeight: "600",
                          fontSize: "14px",
                        }}
                      >
                        {(
                          (emp.first_name?.[0] || "") +
                          (emp.last_name?.[0] || "")
                        ).toUpperCase()}
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: "15px",
                            fontWeight: "500",
                            color: "#334155",
                          }}
                        >
                          {emp.first_name} {emp.last_name}
                        </div>
                        <div style={{ fontSize: "13px", color: "#94a3b8" }}>
                          {emp.role}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        padding: "6px 12px",
                        borderRadius: "8px",
                        background: "#10b981",
                        color: "white",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      {addingEmployee === emp.id ? "Adding..." : "Add"}
                    </div>
                  </button>
                ))
              )}
            </div>

            <button
              onClick={() => setShowAddCrew(false)}
              style={{
                marginTop: "16px",
                padding: "14px",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                background: "white",
                fontSize: "15px",
                fontWeight: "600",
                color: "#64748b",
                cursor: "pointer",
                width: "100%",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Cost Codes Modal */}
      {showCostCodes && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
            }}
            onClick={() => setShowCostCodes(false)}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "430px",
              margin: "0 auto",
              background: "white",
              borderRadius: "24px 24px 0 0",
              padding: "24px",
              paddingBottom: "40px",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "4px",
                background: "#cbd5e1",
                borderRadius: "2px",
                margin: "0 auto 16px",
              }}
            />

            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                }}
              >
                Cost Codes for {job.id}
              </div>
              <div
                style={{ fontSize: "14px", color: "#64748b", marginTop: "4px" }}
              >
                Manage cost codes assigned to this job
              </div>
            </div>

            {loadingCostCodes ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  color: "#64748b",
                }}
              >
                Loading...
              </div>
            ) : (
              <div style={{ flex: 1, overflowY: "auto" }}>
                {/* Current Job Cost Codes */}
                <div style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      marginBottom: "8px",
                    }}
                  >
                    Active on Job ({jobCostCodes.length})
                  </div>
                  {jobCostCodes.length === 0 ? (
                    <div
                      style={{
                        padding: "12px",
                        background: "#f8fafc",
                        borderRadius: "8px",
                        color: "#94a3b8",
                        fontSize: "14px",
                      }}
                    >
                      No cost codes assigned yet
                    </div>
                  ) : (
                    <div
                      style={{
                        background: "#f8fafc",
                        borderRadius: "12px",
                        overflow: "hidden",
                      }}
                    >
                      {jobCostCodes.map((jcc, index) => (
                        <div
                          key={jcc.id}
                          style={{
                            padding: "12px 16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderBottom:
                              index < jobCostCodes.length - 1
                                ? "1px solid #e2e8f0"
                                : "none",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: "15px",
                                fontWeight: "600",
                                color: "#334155",
                              }}
                            >
                              {jcc.cost_code?.code} - {jcc.cost_code?.name}
                            </div>
                            <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                              {jcc.cost_code?.unit} • {jcc.source}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveCostCode(jcc.id)}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "8px",
                              background: "#fee2e2",
                              color: "#dc2626",
                              border: "none",
                              fontSize: "12px",
                              fontWeight: "600",
                              cursor: "pointer",
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Available Cost Codes to Add */}
                <div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      marginBottom: "8px",
                    }}
                  >
                    Available to Add ({availableCostCodes.length})
                  </div>
                  {availableCostCodes.length === 0 ? (
                    <div
                      style={{
                        padding: "12px",
                        background: "#f0fdf4",
                        borderRadius: "8px",
                        color: "#166534",
                        fontSize: "14px",
                      }}
                    >
                      All cost codes are assigned to this job
                    </div>
                  ) : (
                    <div
                      style={{
                        background: "white",
                        borderRadius: "12px",
                        border: "1px solid #e2e8f0",
                        overflow: "hidden",
                      }}
                    >
                      {availableCostCodes.map((cc, index) => (
                        <button
                          key={cc.id}
                          onClick={() => handleAddCostCode(cc)}
                          disabled={addingCostCode === cc.id}
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            background:
                              addingCostCode === cc.id
                                ? "#f1f5f9"
                                : "transparent",
                            border: "none",
                            borderBottom:
                              index < availableCostCodes.length - 1
                                ? "1px solid #f1f5f9"
                                : "none",
                            cursor: addingCostCode ? "wait" : "pointer",
                            textAlign: "left",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: "15px",
                                fontWeight: "500",
                                color: "#334155",
                              }}
                            >
                              {cc.code} - {cc.name}
                            </div>
                            <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                              {cc.unit}
                            </div>
                          </div>
                          <div
                            style={{
                              padding: "6px 12px",
                              borderRadius: "8px",
                              background: "#8b5cf6",
                              color: "white",
                              fontSize: "12px",
                              fontWeight: "600",
                            }}
                          >
                            {addingCostCode === cc.id ? "Adding..." : "Add"}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <button
              onClick={() => setShowCostCodes(false)}
              style={{
                marginTop: "16px",
                padding: "14px",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                background: "white",
                fontSize: "15px",
                fontWeight: "600",
                color: "#64748b",
                cursor: "pointer",
                width: "100%",
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Voice Time Edit Preview Modal */}
      {voiceResult && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 70,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
            }}
            onClick={handleVoiceCancel}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "400px",
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            <TimeEditPreview
              result={voiceResult}
              crew={crew}
              allEmployees={allEmployees}
              onConfirm={handleVoiceConfirm}
              onCancel={handleVoiceCancel}
            />
          </div>
        </div>
      )}

      {/* Universal Voice Result Preview Modal */}
      {universalVoiceResult && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 70,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
            }}
            onClick={handleUniversalVoiceCancel}
          />
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "400px",
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            <UniversalVoicePreview
              result={universalVoiceResult}
              crew={crew}
              onConfirm={async (intent, data) => {
                // Handle based on intent
                if (intent === "production") {
                  // Save production entries
                  try {
                    const entries = data.entries || [];
                    const savedCount = { success: 0, failed: 0 };

                    for (const entry of entries) {
                      // Try to match item to a cost code
                      const matchedCostCode = jobCostCodes.find((jcc) => {
                        const ccName = (
                          jcc.cost_code?.name || ""
                        ).toLowerCase();
                        const ccCode = (
                          jcc.cost_code?.code || ""
                        ).toLowerCase();
                        const item = (entry.item || "").toLowerCase();
                        const hint = (entry.cost_code_hint || "").toLowerCase();
                        return (
                          ccName.includes(item) ||
                          item.includes(ccName) ||
                          ccCode.includes(item) ||
                          (hint &&
                            (ccName.includes(hint) || ccCode.includes(hint)))
                        );
                      });

                      if (matchedCostCode?.cost_code?.id) {
                        await addProduction({
                          orgId,
                          jobId: job.rawId || job.id,
                          costCodeId: matchedCostCode.cost_code.id,
                          quantity: entry.quantity,
                          enteredBy: currentUser?.id,
                          notes:
                            `Voice: ${entry.quantity} ${entry.unit || ""} ${entry.item}`.trim(),
                        });
                        savedCount.success++;
                      } else {
                        console.warn("No matching cost code for:", entry.item);
                        savedCount.failed++;
                      }
                    }

                    if (savedCount.success > 0) {
                      toast.success(
                        `Saved ${savedCount.success} production ${savedCount.success === 1 ? "entry" : "entries"}!${savedCount.failed > 0 ? ` (${savedCount.failed} could not be matched to a cost code)` : ""}`,
                      );
                    } else {
                      toast.warning(
                        "Could not match any items to cost codes. Please add production manually.",
                      );
                    }
                  } catch (err) {
                    console.error("Failed to save production:", err);
                    toast.error("Failed to save: " + err.message);
                  }
                } else if (intent === "status_update") {
                  // Save status update
                  try {
                    const { postStatusUpdate } =
                      await import("../lib/supabase");
                    await postStatusUpdate({
                      orgId,
                      jobId: job.rawId || job.id,
                      postedBy: currentUser?.id,
                      updateType: "progress",
                      content:
                        data.summary ||
                        data.work_completed?.join(". ") ||
                        universalVoiceResult.transcript,
                    });
                    toast.success("Status update saved!");
                  } catch (err) {
                    console.error("Failed to save status:", err);
                    toast.error("Failed to save: " + err.message);
                  }
                } else if (intent === "time_allocation") {
                  // Navigate to allocation page with voice data
                  onNavigate?.("time", {
                    voiceAllocation: universalVoiceResult,
                  });
                }
                setUniversalVoiceResult(null);
              }}
              onCancel={handleUniversalVoiceCancel}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * UniversalVoicePreview - Shows detected intent and extracted data for confirmation
 */
function UniversalVoicePreview({ result, crew, onConfirm, onCancel }) {
  const { transcript, extracted, confidence } = result || {};
  const detectedIntent = extracted?.detected_intent || "unknown";
  const confidencePercent = Math.round((confidence || 0) * 100);

  const intentLabels = {
    time_edit: "Time Edit",
    time_allocation: "Time Allocation",
    production: "Production",
    status_update: "Status Update",
    issue: "Issue Report",
    note: "Note",
    unknown: "Unknown",
  };

  const intentColors = {
    time_edit: { bg: "#dbeafe", color: "#1d4ed8" },
    time_allocation: { bg: "#dcfce7", color: "#166534" },
    production: { bg: "#e0e7ff", color: "#4338ca" },
    status_update: { bg: "#fef3c7", color: "#92400e" },
    issue: { bg: "#fee2e2", color: "#dc2626" },
    note: { bg: "#f1f5f9", color: "#475569" },
    unknown: { bg: "#f1f5f9", color: "#64748b" },
  };

  const colors = intentColors[detectedIntent] || intentColors.unknown;

  // Get the relevant data based on intent
  const getData = () => {
    if (detectedIntent === "production" && extracted?.production) {
      return extracted.production;
    }
    if (detectedIntent === "status_update" && extracted?.status_update) {
      return extracted.status_update;
    }
    if (detectedIntent === "time_allocation" && extracted?.time_allocation) {
      return extracted.time_allocation;
    }
    if (detectedIntent === "issue" && extracted?.issue) {
      return extracted.issue;
    }
    if (detectedIntent === "note" && extracted?.note) {
      return extracted.note;
    }
    return null;
  };

  const data = getData();

  return (
    <div
      style={{
        background: "white",
        borderRadius: "16px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px",
          borderBottom: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "16px",
            fontWeight: "600",
            color: "#1e293b",
          }}
        >
          Voice Command
        </h3>
        <span
          style={{
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: "600",
            background: colors.bg,
            color: colors.color,
          }}
        >
          {intentLabels[detectedIntent]}
        </span>
      </div>

      <div style={{ padding: "16px" }}>
        {/* Transcript */}
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontSize: "11px",
              fontWeight: "600",
              color: "#64748b",
              textTransform: "uppercase",
              marginBottom: "6px",
            }}
          >
            What you said
          </label>
          <p
            style={{
              margin: 0,
              padding: "12px",
              background: "#f8fafc",
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              fontSize: "14px",
              color: "#475569",
              fontStyle: "italic",
            }}
          >
            "{transcript || "No transcript available"}"
          </p>
        </div>

        {/* Extracted Data */}
        {detectedIntent === "production" && data && (
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "11px",
                fontWeight: "600",
                color: "#64748b",
                textTransform: "uppercase",
                marginBottom: "6px",
              }}
            >
              Production Entries
            </label>
            <div
              style={{
                background: "#e0e7ff",
                borderRadius: "10px",
                padding: "12px",
                border: "1px solid #a5b4fc",
              }}
            >
              {data.entries?.map((entry, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 0",
                    borderBottom:
                      i < data.entries.length - 1
                        ? "1px solid #c7d2fe"
                        : "none",
                  }}
                >
                  <div>
                    <span
                      style={{
                        fontSize: "18px",
                        fontWeight: "700",
                        color: "#4338ca",
                      }}
                    >
                      {entry.quantity}
                    </span>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#4338ca",
                        marginLeft: "6px",
                      }}
                    >
                      {entry.unit || ""}
                    </span>
                    <span
                      style={{
                        fontSize: "15px",
                        fontWeight: "500",
                        color: "#312e81",
                        marginLeft: "8px",
                      }}
                    >
                      {entry.item}
                    </span>
                  </div>
                  {entry.cost_code_hint && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#6366f1",
                        background: "#eef2ff",
                        padding: "2px 8px",
                        borderRadius: "6px",
                      }}
                    >
                      {entry.cost_code_hint}
                    </span>
                  )}
                </div>
              ))}
              {data.summary && (
                <div
                  style={{
                    marginTop: "8px",
                    fontSize: "13px",
                    color: "#4338ca",
                    fontStyle: "italic",
                  }}
                >
                  {data.summary}
                </div>
              )}
            </div>
          </div>
        )}

        {detectedIntent === "status_update" && data && (
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "11px",
                fontWeight: "600",
                color: "#64748b",
                textTransform: "uppercase",
                marginBottom: "6px",
              }}
            >
              Status Update
            </label>
            <div
              style={{
                background: "#fef3c7",
                borderRadius: "10px",
                padding: "12px",
                border: "1px solid #fcd34d",
              }}
            >
              {data.summary && (
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: "500",
                    color: "#92400e",
                    marginBottom: "8px",
                  }}
                >
                  {data.summary}
                </div>
              )}
              {data.work_completed?.length > 0 && (
                <ul
                  style={{ margin: 0, paddingLeft: "20px", color: "#78350f" }}
                >
                  {data.work_completed.map((item, i) => (
                    <li
                      key={i}
                      style={{ fontSize: "14px", marginBottom: "4px" }}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              )}
              {data.quantities?.length > 0 && (
                <div
                  style={{
                    marginTop: "8px",
                    fontSize: "13px",
                    color: "#92400e",
                  }}
                >
                  {data.quantities.map((q, i) => (
                    <span key={i} style={{ marginRight: "12px" }}>
                      {q.qty} {q.unit || ""} {q.item}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {detectedIntent === "time_allocation" && data && (
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "11px",
                fontWeight: "600",
                color: "#64748b",
                textTransform: "uppercase",
                marginBottom: "6px",
              }}
            >
              Time Allocations
            </label>
            <div
              style={{
                background: "#dcfce7",
                borderRadius: "10px",
                padding: "12px",
                border: "1px solid #86efac",
              }}
            >
              {data.allocations?.map((alloc, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: "14px",
                    color: "#166534",
                    marginBottom: "6px",
                  }}
                >
                  <strong>{alloc.employee_name}</strong>: {alloc.hours}h{" "}
                  {alloc.activity || ""}
                </div>
              ))}
              <div
                style={{ marginTop: "8px", fontSize: "12px", color: "#15803d" }}
              >
                Go to Allocate screen to save
              </div>
            </div>
          </div>
        )}

        {detectedIntent === "issue" && data && (
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "11px",
                fontWeight: "600",
                color: "#64748b",
                textTransform: "uppercase",
                marginBottom: "6px",
              }}
            >
              Issue
            </label>
            <div
              style={{
                background: "#fee2e2",
                borderRadius: "10px",
                padding: "12px",
                border: "1px solid #fca5a5",
              }}
            >
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: "500",
                  color: "#dc2626",
                }}
              >
                {data.description}
              </div>
              {data.urgency && data.urgency !== "low" && (
                <span
                  style={{
                    display: "inline-block",
                    marginTop: "8px",
                    padding: "2px 8px",
                    borderRadius: "6px",
                    background: data.urgency === "high" ? "#dc2626" : "#f97316",
                    color: "white",
                    fontSize: "11px",
                    fontWeight: "600",
                    textTransform: "uppercase",
                  }}
                >
                  {data.urgency}
                </span>
              )}
            </div>
          </div>
        )}

        {detectedIntent === "note" && data && (
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "11px",
                fontWeight: "600",
                color: "#64748b",
                textTransform: "uppercase",
                marginBottom: "6px",
              }}
            >
              Note
            </label>
            <div
              style={{
                background: "#f1f5f9",
                borderRadius: "10px",
                padding: "12px",
                border: "1px solid #cbd5e1",
                fontSize: "14px",
                color: "#475569",
              }}
            >
              {data.content}
            </div>
          </div>
        )}

        {/* Confidence */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "600",
              color: "#64748b",
              textTransform: "uppercase",
            }}
          >
            Confidence
          </span>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                flex: 1,
                background: "#e2e8f0",
                borderRadius: "4px",
                height: "8px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  borderRadius: "4px",
                  background:
                    confidence > 0.8
                      ? "#22c55e"
                      : confidence > 0.5
                        ? "#eab308"
                        : "#ef4444",
                  width: `${confidencePercent}%`,
                }}
              />
            </div>
            <span
              style={{ fontSize: "14px", fontWeight: "600", color: "#475569" }}
            >
              {confidencePercent}%
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          padding: "16px",
          borderTop: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <button
          onClick={onCancel}
          style={{
            flex: 1,
            padding: "12px 16px",
            borderRadius: "10px",
            border: "1px solid #e2e8f0",
            background: "white",
            fontSize: "14px",
            fontWeight: "600",
            color: "#64748b",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          onClick={() => onConfirm(detectedIntent, data)}
          style={{
            flex: 2,
            padding: "12px 16px",
            borderRadius: "10px",
            border: "none",
            background: colors.color,
            fontSize: "14px",
            fontWeight: "600",
            color: "white",
            cursor: "pointer",
          }}
        >
          {detectedIntent === "production"
            ? "Save Production"
            : detectedIntent === "status_update"
              ? "Save Update"
              : detectedIntent === "time_allocation"
                ? "Go to Allocate"
                : detectedIntent === "issue"
                  ? "Report Issue"
                  : "Save"}
        </button>
      </div>
    </div>
  );
}
